/**
 * Storefront (shopper) surface — canonical shopper SESSION paths live
 * under `/shop/:slug/*` (convergence card q7EQ3a0b).
 *
 * History: PR-R1 staged this group with legacy flat paths (`/orders`,
 * `/cart`, ...). PR-R5 promoted them to the nested `/store/:slug/*`
 * form. The storefront-redesign convergence then moved the whole
 * shopper session tree (cart, signin, orders, addresses,
 * payment-methods) to `/shop/:slug/*` so it sits beside the P5/P6
 * Home/Category/Product tree already at `/shop/:slug[...]`. Both the
 * legacy flat paths AND the intermediate `/store/:slug/*` session paths
 * remain as backward-compat redirects in `redirects.js`.
 *
 * URL refinement only — no feature changes, no schema changes. The
 * `:slug` segment is the same value historically called `storeUrl` in
 * `localStorage` and `:id` in the legacy `/shop/:id` URL (a numeric
 * campaign ID until the slug column lands as a separate schema concern).
 *
 * Every route carries `meta.layout = 'storefront'`. The storefront
 * shell is mounted by `StorefrontLayout.vue`.
 */

// P8 (storefront convergence, card q7EQ3a0b): the legacy single-page
// storefront ENTRY (route custom_shop at /store/:id) and the flat product
// gallery + detail (at /store/:slug/products[...]) are RETIRED. Both
// surfaces now redirect into the canonical /shop/<slug> tree
// (router/redirects.js). Their components were the follow-up deletion the
// P8 note promised and are now GONE from disk.
//
// Phase D (Categories & Storefront, ADR 0059, card a7j1VV6D): the bare
// `/shop` (generic_shop) landing that mounted StorefrontViewShopper is ALSO
// now retired -- it was the last surface still reading the legacy
// campaign.product_categories string list / storefrontCategoryGrouping
// helper. StorefrontViewShopper.vue is deleted; `/shop` redirects (see
// router/redirects.js) into the canonical entity-driven /shop/<slug> tree
// below instead of mounting a component here.
import StorefrontSignIn from "@/components/Storefront/StorefrontSignIn";
import StorefrontCart from "@/components/Storefront/StorefrontCart";
import StorefrontOrderConfirmation from "@/components/Storefront/StorefrontOrderConfirmation";
import StorefrontOrderPayment from "@/components/Storefront/StorefrontOrderPayment";
import StorefrontOrdersList from "@/components/Storefront/StorefrontOrdersList";
import StorefrontOrderDetail from "@/components/Storefront/StorefrontOrderDetail";
import StorefrontSavedAddresses from "@/components/Storefront/StorefrontSavedAddresses";
// Personal Profile page -- the ONE shared component, mounted in the
// storefront chrome (card https://trello.com/c/BxskXBad).
import PersonalProfilePage from "@/pages/PersonalProfilePage.vue";
import StorefrontSavedPaymentMethods from "@/components/Storefront/StorefrontSavedPaymentMethods";
// Plan 2 buyer-flow closure — session gate wrapping the gallery +
// detail surfaces. Establishes the shopper session (token swap) BEFORE
// the wrapped page renders so the cart machinery works unchanged.
import StorefrontSessionGate from "@/components/Storefront/StorefrontSessionGate";
// P5 (Live Storefront - Home/Category/Product, ADR 0060/0061,
// https://trello.com/c/q7EQ3a0b): the canonical 3-level storefront tree.
// Home + Category pages mount UNDER StorefrontSessionGate (single
// session/preview establish-point) at the Q7-canonical /shop/<slug>[/c/<slug>]
// paths -- the exact shape StoreCategories/CategoryCard.vue's Copy-link URL
// builds, so editor links resolve. The Product page (/p/<id>) is P6.
import StorefrontHomePage from "@/pages/store/HomePage";
import StorefrontCategoryPage from "@/pages/store/CategoryPage";
// P6 (Live Storefront - Home/Category/Product, ADR 0060/0061,
// https://trello.com/c/q7EQ3a0b): the Product page (gallery + buy box + size x
// quantity matrix + "More in <Category>" rail) at the Q7-canonical
// /shop/<slug>/c/<categorySlug>/p/<id> path. Mounts under the SAME
// StorefrontSessionGate as Home + Category (single session/preview establish
// point; children never swap tokens; ?preview=1 skips the swap in the gate).
import StorefrontProductPage from "@/pages/store/ProductPage";
// Storefront SEARCH results page (card https://trello.com/c/HUIrLhaU,
// pebble "Search"). Mounts under the SAME StorefrontSessionGate as Home /
// Category / Product -- search must not be a way around the shopper session
// or the store password gate.
import StorefrontSearchPage from "@/pages/store/SearchPage";
// A store's hosted Privacy / Refund policy page (Jake, 2026-10-19). Mounts
// under the SAME StorefrontSessionGate as Home / Category / Search so the
// session, the store password gate and the preview skip apply identically.
import StorefrontPolicyPage from "@/pages/store/PolicyPage";
// The shared-password WALL for a protected store. The router guard
// (router/storefrontPasswordGate.js) redirects here for any /shop/:slug/*
// surface of a password-protected store the visitor has not yet cleared.
//
// It is a SIBLING route, not a child of the session gate below, and that is
// deliberate: the guard must be able to send the visitor here from ANY
// storefront page (including the cart / orders / profile siblings), and a
// destination nested inside the gated subtree could not serve that purpose.
import StorefrontPasswordPage from "@/pages/store/StorefrontPasswordPage";
// Shopper help (`docs/user/HELP-SYSTEM-DESIGN.md`). Storefront-native and
// PUBLIC -- see the route definitions at the bottom of this file.
import ShopperHelpPage from "@/pages/help/ShopperHelpPage.vue";
// GH #2941 defect 1: the ACCOUNT-AREA guard. The three shopper session
// routes below (profile / addresses / payment-methods) held personal data
// behind no guard at all, and the storefront auto-provisions every anonymous
// visitor into the store's SHARED shopper account -- so a real token exists
// and `requireToken` is satisfied. See router/storefrontAccountGuard.js for
// why the distinction cannot be derived and must be read from the marker.
import { createStorefrontAccountGuard } from "@/router/storefrontAccountGuard";

// ONE guard instance shared by the three routes: it is stateless, and a
// single instance makes it obvious at a glance that all three enforce the
// same rule rather than three separately-drifting copies.
const requirePersonalShopperSession = createStorefrontAccountGuard();

const storefrontRoutes = [
    {
        // The password wall. Slug-scoped like every other storefront route so
        // the wall is unmistakably attached to ONE store -- which is also what
        // makes the cleared marker per-store rather than global.
        //
        // Registered FIRST so it is matched before the `/shop/:slug` tree
        // below could ever interpret "password" as a store-relative segment.
        path: "/shop/:slug/password",
        name: "storefront_password_gate",
        component: StorefrontPasswordPage,
        meta: { layout: "storefront" },
    },
    {
        // P5 (Live Storefront, ADR 0060/0061): the canonical 3-level tree.
        // Home + Category pages hang UNDER StorefrontSessionGate so the
        // shopper session + preview-skip are established ONCE (children never
        // swap tokens; ?preview=1 skips the swap in the gate). The Q7 URL
        // model is canonical + verified against CategoryCard.vue.storefrontUrl:
        //   Home:     /shop/<storeSlug>
        //   Category: /shop/<storeSlug>/c/<categorySlug>
        //   Product:  /shop/<storeSlug>/c/<categorySlug>/p/<id>   (P6)
        //
        // This SUPERSEDES the legacy `/shop/:id` -> `/store/:id` redirect
        // (removed from redirects.js): `/shop/<slug>` now IS the canonical
        // Home surface. P8 RETIRED the legacy single scrolling page: its
        // `/store/:id` entry now redirects here (router/redirects.js).
        path: "/shop/:slug",
        component: StorefrontSessionGate,
        meta: { layout: "storefront" },
        children: [
            {
                path: "",
                name: "storefront_home",
                component: StorefrontHomePage,
                meta: { layout: "storefront" },
            },
            {
                // /search?q=<terms> -- the search RESULTS page.
                //
                // The term is a QUERY PARAM, not a path segment: search
                // terms routinely contain spaces and slashes, both of which
                // fight a path segment, and `?q=` keeps the URL shareable.
                //
                // Registered BEFORE the `c/:categorySlug` route below. That
                // ordering is not load-bearing today (this path has no `c/`
                // prefix so the two cannot collide), but it keeps the
                // specific literal path ahead of the parameterized ones,
                // which is the ordering that stays correct if the category
                // route is ever loosened.
                path: "search",
                name: "storefront_search",
                component: StorefrontSearchPage,
                meta: { layout: "storefront" },
            },
            {
                // /privacy-policy and /refund-policy -- the store's hosted
                // policy pages (Jake, 2026-10-19). A store whose policy is a
                // link to its own URL (or whose page is empty) has no page
                // here: the component renders the not-found state. The
                // footer links straight to the URL in that case.
                //
                // `meta.policyKind` is how ONE component serves both routes
                // -- the route states which policy it is, rather than the
                // component sniffing the path.
                path: "privacy-policy",
                name: "storefront_privacy_policy",
                component: StorefrontPolicyPage,
                meta: { layout: "storefront", policyKind: "privacy" },
            },
            {
                path: "refund-policy",
                name: "storefront_refund_policy",
                component: StorefrontPolicyPage,
                meta: { layout: "storefront", policyKind: "refund" },
            },
            {
                // /c/<categorySlug> -- one category. A hidden category still
                // resolves + renders (with a notice); the page component owns
                // that. `categorySlug` is the resolved route param the page
                // reads (distinct from `:slug`, the store slug).
                path: "c/:categorySlug",
                name: "storefront_category",
                component: StorefrontCategoryPage,
                meta: { layout: "storefront" },
            },
            {
                // /c/<categorySlug>/p/<id> -- one product (P6). The buy box
                // (size x quantity matrix) adds multiple sizes in one action
                // reusing the existing per-(color,size) cart contract. `id` is
                // the campaign_product id; `categorySlug` is the category the
                // product is viewed under (drives the breadcrumb + rail).
                //
                // `categorySlug` matches the EMPTY string too (the `(...)`
                // custom-regex form; a bare `:categorySlug` requires at least
                // one character and would not match `/c//p/<id>`). That is
                // deliberate and load-bearing: the HOME category's slug IS
                // empty. Home lives at /shop/<slug> and owns no /c/ segment,
                // so its StoreCategory row carries slug ''. The BACKEND
                // already speaks this convention -- _resolve_category in
                // api/src/endpoints/storefront_products.py maps an empty/root
                // category_slug to the is_home=True row -- and so does
                // useStorefrontCategory, which sends the bare /category path
                // for an empty slug. Before this, the router was the ONE layer
                // that could not express it: StorefrontProductGrid composes
                // /c/<categorySlug>/p/<id> and HomePage mounts it with
                // :category-slug="''", so every card on the Home grid linked
                // to /c//p/<id>, matched no route, and fell through to the SPA
                // not-found -- the entire home grid, the first thing a shopper
                // sees.
                //
                // Keeping the empty slug (rather than inventing a `/c/home/`
                // sentinel segment) keeps ONE mechanism: the same empty-string
                // = HOME convention the backend, the composable, and the
                // category nav's :active-slug="''" all already use. A sentinel
                // would add a second source of truth for "which category is
                // this product viewed under" and force a translation layer in
                // ProductPage, the breadcrumb, and the QR deep-link endpoint
                // (docs/design-philosophy.md: one mechanism per concept,
                // single-source state).
                path: "c/:categorySlug([^/]*)/p/:id",
                name: "storefront_product",
                component: StorefrontProductPage,
                meta: { layout: "storefront" },
            },

            // ---- Shopper SESSION pages (2026-10-02) --------------------------
            //
            // Plan: docs/planning/2026-10-02-storefront-session-everywhere-plan.md
            //
            // These used to be SIBLING route records with no gate, so a cold
            // load of the cart rendered blank and orders/addresses bounced to
            // the generic /login (AdventHealth, prod 2026-10-02). They are now
            // children of the gate, which establishes the session (token AND
            // store id) before any of them renders.
            //
            // `meta.session` states what each page needs:
            //   "anonymous-ok" -- works for an anonymous visitor (the orders
            //                     list shows its built-in "find your order"
            //                     lookup; order detail reads an emailed link
            //                     through its one-order code);
            //   "account"      -- personal data; a visitor without a personal
            //                     session is sent to the STORE HOME by
            //                     `requirePersonalShopperSession` (GH #2941).
            //
            // Route `name:` values are unchanged, so named navigation works.
            {
                path: "signin",
                name: "sign-in-test",
                component: StorefrontSignIn,
                meta: { layout: "storefront", session: "anonymous-ok" },
            },
            {
                path: "cart",
                name: "storefront_cart",
                component: StorefrontCart,
                meta: { layout: "storefront", session: "anonymous-ok" },
            },
            {
                path: "orders",
                name: "storefront_orders_list",
                component: StorefrontOrdersList,
                meta: { layout: "storefront", session: "anonymous-ok" },
            },
            {
                // The emailed order link (`?token=` -- the one-order code).
                // The code wins over the session the gate establishes: the
                // page reads the order through the guest endpoint whenever
                // the link carries one (orderDetailHelpers.guestOrderRequest).
                path: "orders/:id",
                name: "storefront_order_detail",
                component: StorefrontOrderDetail,
                meta: { layout: "storefront", session: "anonymous-ok" },
            },
            {
                path: "orders/:id/confirmation",
                name: "storefront_order_confirmation",
                component: StorefrontOrderConfirmation,
                meta: { layout: "storefront", session: "anonymous-ok" },
            },
            {
                // Personal Profile (card https://trello.com/c/BxskXBad) -- the
                // ONE shared page, mounted in the storefront chrome. Logged-in
                // shoppers only; guests reach their orders through the
                // tokenized link in the confirmation email.
                path: "profile",
                name: "storefront_profile",
                component: PersonalProfilePage,
                meta: { layout: "storefront", session: "account" },
                beforeEnter: requirePersonalShopperSession,
            },
            {
                path: "addresses",
                name: "storefront_saved_addresses",
                component: StorefrontSavedAddresses,
                meta: { layout: "storefront", session: "account" },
                beforeEnter: requirePersonalShopperSession,
            },
            {
                path: "payment-methods",
                name: "storefront_saved_payment_methods",
                component: StorefrontSavedPaymentMethods,
                meta: { layout: "storefront", session: "account" },
                beforeEnter: requirePersonalShopperSession,
            },
        ],
    },
    // P8 (storefront convergence, card q7EQ3a0b): the legacy FLAT product
    // gallery + detail (`/store/:slug/products` and
    // `/store/:slug/products/:id`) were RETIRED. The
    // canonical product surfaces are the P5/P6 Category + Product pages under
    // `/shop/:slug/*` above. Both legacy paths now redirect to the store's
    // `/shop/<slug>` Home (router/redirects.js) so old bookmarks don't 404.
    //
    // Storefront convergence (card q7EQ3a0b): the shopper SESSION tree
    // (signin, cart, orders, order detail/confirmation, addresses,
    // payment-methods) moves from the legacy `/store/:slug/*` form to
    // `/shop/:slug/*` so the whole shopper journey -- Home/Category/Product
    // (above, under StorefrontSessionGate) PLUS the cart/checkout session --
    // lives under a single `/shop/<slug>` prefix. Route `name:` values are
    // kept STABLE (only `path` changes) so named-nav callers are unaffected;
    // in-page navigation uses hardcoded path strings, repointed to /shop.
    // The legacy `/store/:slug/*` session paths are preserved as backward-
    // compat REDIRECTS (router/redirects.js) so old links / emails / bookmarks
    // don't 404. P8 also retired the legacy single-page `/store/:id`
    // (custom_shop) and the flat `/store/:slug/products` gallery -- both now
    // redirect into this `/shop/<slug>` tree (router/redirects.js).
    {
        // Payment remediation -- the emailed "pay for this order" link.
        // Plan: docs/planning/2026-09-23-shopper-payment-remediation-plan.md
        //
        // OUTSIDE the session gate, and with NO `beforeEnter`, DELIBERATELY.
        // This page's audience is a shopper with NO ACCOUNT: guest checkout
        // is the default, so the recipient of a charge-failure email usually
        // cannot log in, and the capability token in the query string IS the
        // authorization (resolved server-side, scoped to this one order).
        // Neither a storefront session nor `requirePersonalShopperSession`
        // has anything to add, and the guard would redirect anonymous
        // storefront sessions to store sign-in and break the flow for exactly
        // the people it exists to serve. Jake's decision 4, 2026-09-23; kept
        // outside the gate by the 2026-10-02 session plan.
        path: "/shop/:slug/orders/:id/payment",
        name: "storefront_order_payment",
        component: StorefrontOrderPayment,
        meta: { layout: "storefront" },
    },

    // ---- Shopper help (in-app hosted help) ---------------------------
    //
    // `docs/user/HELP-SYSTEM-DESIGN.md` decision 4: shopper help is
    // storefront-native and PUBLIC -- it must render with NO distributor
    // login. The `requireToken` allowlist entries (`/help/shopper` exact +
    // `/help/shopper/` prefix) are what make that true; the boundary is
    // locked by `router/__tests__/help-routes-auth-boundary.spec.js`.
    //
    // NOT slug-scoped: help is the same product documentation for every
    // store, so `/shop/:slug/help` would imply a per-store variation that
    // does not exist. It lives on the storefront SURFACE (storefront
    // layout) without living inside a single store's session tree.
    //
    // The `:slug` param is a plain route param resolved against the help
    // API -- routes derive from FILES (design decision 3), so adding
    // `docs/user/shopper/<name>.md` publishes a page with no route edit.
    //
    // `hideBrandMasthead` -- WHITE LABEL. Jake, 2026-09-20: "The help pages
    // on the store shouldn't have the sticky header that links to
    // ThreadFlow. (The header can stay on the SA and Distributor help
    // pages.)" These two routes are the store-facing half of the help
    // surface, so they are the two that carry the flag.
    //
    // It is a ROUTE property, not a path test, on purpose. `HelpLayout` is
    // shared by all three audiences and cannot tell them apart from its own
    // markup; the audience is a fact about which route is mounted, so the
    // route states it. Sniffing `path.startsWith('/help/shopper')` would be
    // correct only until a shopper help route moves -- and that is not
    // hypothetical here: the OWNER help routes are declared as
    // `/help/owner` in router/index.js and served at
    // `/distributor/help/owner`, because they mount inside
    // `withPrefix('/distributor', ...)`. `helpFramed.js`'s HELP_PREFIXES
    // already carries that scar. A path rule written against a declared
    // path is wrong on arrival, and it fails SILENTLY -- the page still
    // renders, it just leaks the brand again.
    //
    // Read by `layouts/HelpLayout.vue`, which fails safe the other way: an
    // absent or non-`true` flag renders the masthead. So a help route added
    // without thinking about audience keeps ThreadFlow's identity, and
    // hiding is always the deliberate, opt-in act.
    {
        path: "/help/shopper",
        name: "shopper_help_index",
        component: ShopperHelpPage,
        meta: { layout: "storefront", hideBrandMasthead: true },
    },
    {
        path: "/help/shopper/:slug",
        name: "shopper_help_article",
        component: ShopperHelpPage,
        meta: { layout: "storefront", hideBrandMasthead: true },
    },
];

export default storefrontRoutes;
